import axios, { AxiosRequestConfig } from "axios";

import { boundsBgsToLatLng, boundsLatLngToBgs, coordsBgsToLatLng } from "../Features/MapComponents/geoUtils";
import { IPlaceSearchResult, IPlaceSearchService } from "../Features/Search/placeSearchTypes";
import axiosRetry from "axios-retry";


type OSMapsPlacesResponse = {
    results: OSMapsPlacesResultItem[]
}

type OSMapsPlacesResultItem = {
    GAZETTEER_ENTRY: OSMapsPlacesGazetteerEntry
}

type OSMapsPlacesGazetteerEntry = {
    ID: string;
    NAME1: string;
    LOCAL_TYPE: string;
    DISTRICT_BOROUGH: string;
    COUNTY_UNITARY: string;
    COUNTRY: string;
    POSTCODE_DISTRICT: string;
    REGION: string;
    TYPE: string;
    GEOMETRY_X: number;
    GEOMETRY_Y: number;
    MBR_XMIN: number;
    MBR_YMIN: number;
    MBR_XMAX: number;
    MBR_YMAX: number;
}


export class OSPlaceSearchService implements IPlaceSearchService {

    private static readonly typesToIgnore = ['other'];

    constructor(private endpointUrl: string) {
    }

    public search(terms: string, biasBounds?: L.LatLngBounds, maxResults?: number, abort?: AbortController): Promise<IPlaceSearchResult[]> {

        if (!terms || terms.length === 0)
            throw new Error('No search terms provided');

        const bounds = !!biasBounds ? boundsLatLngToBgs(biasBounds).join(',') : undefined;

        const client = axios.create();
        axiosRetry(client, { retries: 8, retryDelay: (retryNumber, error) => axiosRetry.exponentialDelay(retryNumber, error, 1000) });

        const requestConfig: AxiosRequestConfig = {
            signal: abort?.signal,
            params: {
                query: terms,
                format: 'JSON',
                maxresults: maxResults ?? 100,
                bounds
            }
        };

        const promise = new Promise<IPlaceSearchResult[]>((resolve, reject) => {
            client.get(this.endpointUrl, requestConfig)
                .then((response) => {
                    const rawResults = (response.data as OSMapsPlacesResponse).results.filter(r => OSPlaceSearchService.typesToIgnore.indexOf(r.GAZETTEER_ENTRY.TYPE) === -1);
                    const mappedResults = rawResults.map(r => this.mapApiResult(r));
                    resolve(mappedResults);
                })
                .catch(reject);
        });

        return promise;

    }

    private mapApiResult(item: OSMapsPlacesResultItem): IPlaceSearchResult {
        return {
            id: item.GAZETTEER_ENTRY.ID,
            name: item.GAZETTEER_ENTRY.NAME1,
            district: item.GAZETTEER_ENTRY.DISTRICT_BOROUGH,
            county: item.GAZETTEER_ENTRY.COUNTY_UNITARY,
            postCodeDistrict: item.GAZETTEER_ENTRY.POSTCODE_DISTRICT,
            region: item.GAZETTEER_ENTRY.REGION,
            country: item.GAZETTEER_ENTRY.COUNTRY,
            type: item.GAZETTEER_ENTRY.LOCAL_TYPE,
            centre: coordsBgsToLatLng([item.GAZETTEER_ENTRY.GEOMETRY_X, item.GAZETTEER_ENTRY.GEOMETRY_Y]),
            boundingBox: boundsBgsToLatLng([item.GAZETTEER_ENTRY.MBR_XMIN, item.GAZETTEER_ENTRY.MBR_YMIN, item.GAZETTEER_ENTRY.MBR_XMAX, item.GAZETTEER_ENTRY.MBR_YMAX])
        };
    }
}